'use client';

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import dynamic from 'next/dynamic';
import { Link } from '@/i18n/routing';
import { useTranslations } from 'next-intl';
import { IconArrowUpRight, IconChevronLeft, IconChevronRight } from '@tabler/icons-react';
import HomeClient from '@/app/[locale]/HomeClient';
import LazyMount from '@/components/LazyMount';
import { EXPLORE_CONTENT_FACETS, contentFacetPath } from '@/lib/social/gate-d/taxonomy';
import { SOCIAL_HOME_HERO_MEDIA } from '@/config/social-home';
import { WHATS_NEW_UPDATES } from '@/config/whats-new-updates';
import { requestGeneratorLaunch } from '@/lib/generation/generator-launch';
import { openExploreAction } from '@/lib/social/gate-d/recreate-client';
import useInstantPostPreview from '@/hooks/useInstantPostPreview';
import BrandLockup from '@/components/BrandLockup';
import HomeVideo, { HomeMediaProvider } from '@/components/media/HomeVideo';
import MediaCard from './MediaCard';
import PublicPostPreview from './PublicPostPreview';

const Introduction = dynamic(() => import('@/components/Introduction'), {
  ssr: false,
});
const KeyFeatures = dynamic(() => import('@/components/KeyFeatures'), {
  ssr: false,
});

const HOME_RAILS_CACHE_TTL_MS = 60_000;
let homeRailsCache = null;
let homeRailsCachedAt = 0;
let homeRailsInFlight = null;

function readHomeRailsCache() {
  const lastPublishedAt = typeof window === 'undefined'
    ? 0
    : Number(window.localStorage.getItem('botgo:last-post-published-at') || 0);
  return {
    data: homeRailsCache,
    isFresh: Boolean(homeRailsCache)
      && homeRailsCachedAt >= lastPublishedAt
      && Date.now() - homeRailsCachedAt < HOME_RAILS_CACHE_TTL_MS,
  };
}

function requestHomeRails({ force = false } = {}) {
  const cached = readHomeRailsCache();
  if (!force && cached.isFresh) return Promise.resolve(cached.data);
  if (homeRailsInFlight) return homeRailsInFlight;
  const lastPublishedAt = typeof window === 'undefined'
    ? 0
    : Number(window.localStorage.getItem('botgo:last-post-published-at') || 0);
  const requestUrl = force && lastPublishedAt
    ? `/api/home/rails?publishedAt=${lastPublishedAt}`
    : '/api/home/rails';
  homeRailsInFlight = fetch(requestUrl, {
    credentials: 'same-origin',
    cache: force ? 'reload' : 'default',
  })
    .then(async (response) => {
      const body = await response.json();
      if (!response.ok) throw new Error(body?.error?.code || 'HOME_RAILS_REQUEST_FAILED');
      homeRailsCache = body;
      homeRailsCachedAt = Date.now();
      return body;
    })
    .finally(() => {
      homeRailsInFlight = null;
    });
  return homeRailsInFlight;
}

/**
 * Phase 1 UX closeout v2：Viral Explorer 首页。
 *
 * 修复要点：
 * 1. Hero 主标题 / 副标题正常显示（修复 i18n key 覆盖 bug）。
 * 2. 极简生成器保持原有紧凑长度。
 * 3. What's New：无标题/说明/ViewAll，完整更新数据 + 动态滑动按钮，视口内自动播放视频。
 * 4. Video / Image Inspiration Rail：瀑布流五列，底部 View all 文字按钮。
 * 5. Browse categories：Videos / Images 双 Tab，展示完整分类并居中排列。
 * 6. CTA：背景视频 + mask 遮罩 + 左侧对齐（参考 botgo_ai_old BringIdeasSection）。
 */

function SectionHeader({ title, description }) {
  return (
    <div>
      <h2 className="text-2xl font-semibold sm:text-3xl">{title}</h2>
      {description ? <p className="mt-2 text-sm text-white/55">{description}</p> : null}
    </div>
  );
}

function WhatsNewCard({ update, tWhatsNew, visibleCount = 5, nearby, scrollRef }) {
  const title = tWhatsNew(`updates.${update.translationKey}.title`);
  const summary = tWhatsNew(`updates.${update.translationKey}.summary`);
  const href = update.tryNowHref || '/';
  const isExternal = href.startsWith('http');
  const CardLink = isExternal ? 'a' : Link;

  return (
    <CardLink
      href={href}
      onClick={(event) => requestGeneratorLaunch(event, href)}
      target={isExternal ? '_blank' : undefined}
      rel={isExternal ? 'noopener noreferrer' : undefined}
      data-whats-new-card={update.id}
      className="group min-w-[230px] snap-start overflow-hidden rounded-xl border border-white/10 bg-white/[0.04] transition-all duration-300 hover:border-white/25 focus:outline-none focus-visible:ring-2 focus-visible:ring-amber-300"
      style={{ flex: `0 0 calc((100% - ${visibleCount - 1} * 0.75rem) / ${visibleCount})` }}
    >
      <div className="relative aspect-video w-full overflow-hidden bg-[linear-gradient(115deg,#111827,#1f2937_45%,#111827)]">
        {update.mediaType === 'video' ? (
          <HomeVideo
            src={update.videoSrc}
            poster={update.posterSrc}
            autoPlay
            nearby={nearby}
            nearRootRef={scrollRef}
            className="h-full w-full object-cover"
          />
        ) : (
          // eslint-disable-next-line @next/next/no-img-element
          <img src={update.imageSrc} alt={title} loading="lazy" className="h-full w-full object-cover" />
        )}
      </div>
      <div className="p-3">
        <p className="line-clamp-1 text-sm font-medium text-white">{title}</p>
        <p className="mt-1 line-clamp-2 min-h-8 text-xs leading-4 text-white/50">{summary}</p>
      </div>
    </CardLink>
  );
}

function WhatsNew({ tWhatsNew }) {
  const t = useTranslations('GateD');
  const scrollRef = useRef(null);
  const [visibleCount, setVisibleCount] = useState(7);
  const [warmRange, setWarmRange] = useState({ start: 0, end: 8 });
  const [scrollState, setScrollState] = useState({ canScrollLeft: false, canScrollRight: false });
  // 与 What's New 完整页共用同一份数据；首屏仍只显示响应式列数，其余内容横向浏览。
  const updates = WHATS_NEW_UPDATES;

  const syncScrollState = useCallback(() => {
    const node = scrollRef.current;
    if (!node) return;
    const maxScrollLeft = Math.max(0, node.scrollWidth - node.clientWidth);
    const stride = (node.firstElementChild?.getBoundingClientRect().width || 230) + 12;
    const start = Math.max(0, Math.floor(node.scrollLeft / stride) - 2);
    const end = Math.ceil((node.scrollLeft + node.clientWidth) / stride) + 1;
    setWarmRange((current) => current.start === start && current.end === end ? current : { start, end });
    const nextState = {
      canScrollLeft: node.scrollLeft > 2,
      canScrollRight: node.scrollLeft < maxScrollLeft - 2,
    };
    setScrollState((current) => (
      current.canScrollLeft === nextState.canScrollLeft
        && current.canScrollRight === nextState.canScrollRight
        ? current
        : nextState
    ));
  }, []);

  useEffect(() => {
    const node = scrollRef.current;
    if (!node) return undefined;

    const apply = () => {
      const width = node.clientWidth;
      const nextVisibleCount = width >= 1280
        ? 7
        : width >= 1100
          ? 6
          : width >= 900
            ? 4
            : width >= 640
              ? 3
              : 1;
      setVisibleCount(nextVisibleCount);
      window.requestAnimationFrame(syncScrollState);
    };

    apply();
    node.addEventListener('scroll', syncScrollState, { passive: true });
    const resizeObserver = new ResizeObserver(apply);
    resizeObserver.observe(node);

    return () => {
      node.removeEventListener('scroll', syncScrollState);
      resizeObserver.disconnect();
    };
  }, [syncScrollState]);

  if (!updates.length) return null;

  const scroll = (direction) => {
    const node = scrollRef.current;
    if (!node) return;
    const gap = 12;
    const cardWidth = node.firstElementChild?.getBoundingClientRect().width
      || (node.clientWidth - gap * (visibleCount - 1)) / Math.max(visibleCount, 1);
    const target = node.scrollLeft + direction * (cardWidth + gap) * 2;
    node.scrollTo({
      left: Math.max(0, Math.min(target, node.scrollWidth - node.clientWidth)),
      behavior: 'smooth',
    });
  };

  return (
    <section className="mx-auto max-w-[1500px] px-4 py-8 sm:px-6">
      <div className="relative">
        <div
          ref={scrollRef}
          data-whats-new-rail="true"
          className="flex snap-x snap-mandatory gap-3 overflow-x-auto scrollbar-hide"
          style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
        >
          {updates.map((update, index) => (
            <WhatsNewCard
              key={update.id}
              update={update}
              tWhatsNew={tWhatsNew}
              visibleCount={visibleCount}
              nearby={index >= warmRange.start && index <= warmRange.end}
              scrollRef={scrollRef}
            />
          ))}
        </div>
        {scrollState.canScrollLeft ? (
          <button
            type="button"
            onClick={() => scroll(-1)}
            aria-label={t('home.scrollLeft')}
            className="absolute left-2 top-1/2 z-10 flex h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full border border-white/15 bg-[#0b0f16]/90 text-white/70 shadow-lg backdrop-blur transition-all hover:scale-105 hover:text-white focus:outline-none focus-visible:ring-2 focus-visible:ring-amber-300"
          >
            <IconChevronLeft size={19} />
          </button>
        ) : null}
        {scrollState.canScrollRight ? (
          <button
            type="button"
            onClick={() => scroll(1)}
            aria-label={t('home.scrollRight')}
            className="absolute right-2 top-1/2 z-10 flex h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full border border-white/15 bg-[#0b0f16]/90 text-white/70 shadow-lg backdrop-blur transition-all hover:scale-105 hover:text-white focus:outline-none focus-visible:ring-2 focus-visible:ring-amber-300"
          >
            <IconChevronRight size={19} />
          </button>
        ) : null}
      </div>
    </section>
  );
}

const RAIL_ITEM_LIMIT = 24;

/**
 * 平衡瀑布流：按卡片实际宽高比做贪心最短列分配，
 * 让五列高度尽量接近，避免 CSS columns 竖向填充导致的
 * 首尾列稀疏、中间列过密的畸形分布。
 */
function useResponsiveColumnCount() {
  const containerRef = useRef(null);
  const [columnCount, setColumnCount] = useState(5);

  useEffect(() => {
    const node = containerRef.current;
    if (!node) return undefined;
    const update = () => {
      const width = node.clientWidth;
      setColumnCount(width >= 1280 ? 5 : width >= 900 ? 4 : width >= 640 ? 3 : 2);
    };
    update();
    const observer = new ResizeObserver(update);
    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  return [containerRef, columnCount];
}

function useBalancedColumns(items, columnCount, reserveFirstCard = false) {
  return useMemo(() => {
    const source = (items || []).slice(0, RAIL_ITEM_LIMIT);
    const columns = Array.from({ length: columnCount }, () => []);
    const heights = new Array(columnCount).fill(0);
    if (reserveFirstCard) heights[0] = 1.15;
    for (const item of source) {
      const media = item?.media?.[0];
      const width = Number(media?.width || 0);
      const height = Number(media?.height || 0);
      const ratio = width > 0 && height > 0 ? height / width : 1.25;
      let target = 0;
      for (let i = 1; i < columnCount; i += 1) {
        if (heights[i] < heights[target]) target = i;
      }
      columns[target].push(item);
      heights[target] += ratio;
    }
    return columns;
  }, [columnCount, items, reserveFirstCard]);
}

function MasonryWindow({
  items,
  onOpen,
  onAction,
  viewAllLabel,
  viewAllHref,
  leadingCard = null,
  heightClassName = 'h-[clamp(680px,calc(100vh-7rem),980px)]',
  buttonBottomClassName = 'bottom-7',
}) {
  const [containerRef, columnCount] = useResponsiveColumnCount();
  const columns = useBalancedColumns(items, columnCount, Boolean(leadingCard));
  const [bounds, setBounds] = useState(null);
  useEffect(() => {
    const node = containerRef.current;
    if (!node) return undefined;
    const update = () => {
      const next = {
        width: node.clientWidth,
        height: node.clientHeight,
        lead: node.querySelector('[data-home-leading-card]')?.getBoundingClientRect().height || 0,
        gap: window.innerWidth >= 640 ? 16 : 12,
        minimum: window.innerWidth >= 640 ? 192 : 144,
      };
      setBounds((current) => JSON.stringify(current) === JSON.stringify(next) ? current : next);
    };
    const observer = new ResizeObserver(update);
    observer.observe(node);
    update();
    return () => observer.disconnect();
  }, [columnCount, containerRef, leadingCard]);
  const windowColumns = columns.map((column, columnIndex) => {
    if (!bounds) return [];
    const width = (bounds.width - (columnCount - 1) * bounds.gap) / columnCount;
    let height = columnIndex === 0 && leadingCard ? bounds.lead + bounds.gap : 0;
    return column.filter((item) => {
      const top = height;
      const media = item.media?.[0];
      const ratio = media?.width > 0 && media?.height > 0 ? media.height / media.width : 1.25;
      height += Math.max(bounds.minimum, width * ratio) + bounds.gap;
      return top < bounds.height;
    });
  });

  return (
    <div
      ref={containerRef}
      data-home-masonry="true"
      className={`relative overflow-hidden ${heightClassName}`}
    >
      <div
        className="grid items-start gap-3 sm:gap-4"
        style={{ gridTemplateColumns: `repeat(${columnCount}, minmax(0, 1fr))` }}
      >
        {windowColumns.map((column, columnIndex) => (
          <div key={columnIndex} className="flex min-w-0 flex-col gap-3 sm:gap-4">
            {columnIndex === 0 && leadingCard ? <div data-home-leading-card>{leadingCard}</div> : null}
            {column.map((item) => (
              <MediaCard
                key={item.id}
                item={item}
                homePreview
                onOpen={() => onOpen(item, items)}
                onAction={onAction}
              />
            ))}
          </div>
        ))}
      </div>
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-x-0 bottom-0 h-56 bg-gradient-to-b from-transparent via-[#070a0f]/80 to-[#070a0f]"
      />
      <Link
        href={viewAllHref}
        className={`group absolute left-1/2 z-10 inline-flex -translate-x-1/2 items-center gap-2 whitespace-nowrap rounded-full bg-amber-300 px-7 py-3 text-sm font-semibold text-[#11140d] shadow-[0_16px_48px_rgba(0,0,0,.42)] transition-all duration-300 hover:scale-[1.03] hover:bg-amber-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-white ${buttonBottomClassName}`}
      >
        {viewAllLabel}
        <IconArrowUpRight size={17} className="transition-transform duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
      </Link>
    </div>
  );
}

function MasonryRail({ title, description, items, emptyLabel, onOpen, onAction, viewAllLabel, viewAllHref }) {
  const hasItems = items?.length > 0;

  return (
    <section className="w-full px-3 py-10 sm:px-4 lg:px-5">
      <div className="mb-5">
        <SectionHeader title={title} description={description} />
      </div>
      {hasItems ? (
        <MasonryWindow
          items={items}
          onOpen={onOpen}
          onAction={onAction}
          viewAllLabel={viewAllLabel}
          viewAllHref={viewAllHref}
        />
      ) : (
        <p className="rounded-2xl border border-white/10 p-8 text-white/55">{emptyLabel}</p>
      )}
    </section>
  );
}

function ModelSpotlight({ items, t, onOpen, onAction }) {
  const modelCard = (
    <div className="relative flex min-h-[300px] flex-col justify-between overflow-hidden rounded-2xl border border-amber-300/20 bg-[radial-gradient(circle_at_85%_10%,rgba(251,191,36,.28),transparent_44%),linear-gradient(145deg,#20170d,#0e1117_68%)] p-6 shadow-[0_24px_70px_rgba(0,0,0,.26)]">
      <div>
        <p className="text-xs font-semibold uppercase tracking-[.2em] text-amber-300">{t('home.modelSpotlight')}</p>
        <h3 className="mt-6 whitespace-nowrap text-[clamp(1.5rem,2vw,2.25rem)] font-semibold leading-tight text-white">
          {t('home.seedanceSeries')}
        </h3>
      </div>
      <ul className="space-y-2.5 text-sm text-white/72">
        <li>Seedance 2.5</li>
        <li>Seedance 2.0</li>
        <li>Seedance 2.0 Fast</li>
        <li>Seedance 2.0 Mini</li>
      </ul>
    </div>
  );

  return (
    <section className="w-full px-3 py-10 sm:px-4 lg:px-5">
      <div className="mb-5">
        <SectionHeader
          title={t('home.modelSpotlight')}
          description={t('home.modelSpotlightDescription')}
        />
      </div>
      {items?.length ? (
        <MasonryWindow
          items={items}
          onOpen={onOpen}
          onAction={onAction}
          viewAllLabel={t('home.viewAllCreations')}
          viewAllHref="/explore?mediaType=video&model=seedance-2.0&sort=featured"
          leadingCard={modelCard}
          heightClassName="h-[clamp(560px,62vh,700px)]"
          buttonBottomClassName="bottom-20"
        />
      ) : <p className="rounded-2xl border border-white/10 p-8 text-white/55">{t('common.empty')}</p>}
    </section>
  );
}

function CategoryTiles({ t }) {
  const [mediaType, setMediaType] = useState('video');
  const [group, setGroup] = useState('useCases');
  const facets = EXPLORE_CONTENT_FACETS[mediaType];
  const categoryRows = Array.from({ length: Math.ceil(facets[group].length / 6) }, (_, index) => facets[group].slice(index * 6, index * 6 + 6));
  const tabClass = (selected) => `whitespace-nowrap rounded-full px-2 py-2.5 text-xs font-medium sm:px-5 sm:text-sm transition hover:scale-[1.02] focus-visible:ring-2 focus-visible:ring-amber-300 ${selected ? 'bg-white text-[#0b0f16]' : 'text-white/55 hover:text-white'}`;
  return (
    <section className="w-full px-3 py-16 sm:px-4 sm:py-20 lg:px-5 lg:py-24">
      <div className="mx-auto flex w-fit max-w-full items-center gap-2 sm:gap-4">
        <div role="tablist" aria-label={t('home.categories')} className="flex w-fit gap-0.5 rounded-full border border-white/10 bg-white/[0.04] p-1 sm:gap-1 sm:p-1.5">
          {['video', 'image'].map(type => (
            <button key={type} type="button" role="tab" aria-selected={type === mediaType} onClick={() => setMediaType(type)}
              className={`sm:min-w-28 ${tabClass(type === mediaType)}`}>
              {t(type === 'video' ? 'home.videoCategories' : 'home.imageCategories')}
            </button>
          ))}
        </div>
        <div role="tablist" aria-label={t('home.categories')} className="flex w-fit gap-0.5 rounded-full border border-white/10 bg-white/[0.04] p-1 sm:gap-1 sm:p-1.5">
          {Object.keys(facets).map(value => (
            <button key={value} type="button" role="tab" aria-selected={value === group} onClick={() => setGroup(value)}
              className={tabClass(value === group)}>
              {t(`contentCategories.${value}`)}
            </button>
          ))}
        </div>
      </div>
      <div className="mx-auto mt-8 max-w-[1320px] space-y-3">
        {categoryRows.map((options, index) => (
          <div key={`${mediaType}-${group}-${index}`} className="flex flex-wrap justify-center gap-3">
            {options.map(option => (
              <Link key={option.value} href={contentFacetPath(mediaType, group, option.value)}
                className="inline-flex shrink-0 items-center justify-center whitespace-nowrap rounded-full border border-white/10 bg-white/[0.04] px-5 py-2.5 text-center text-sm text-white/75 transition hover:scale-[1.02] hover:bg-white/[0.08] hover:text-white focus-visible:ring-2 focus-visible:ring-amber-300">
                {t(`taxonomy.${option.value}`)}
              </Link>
            ))}
          </div>
        ))}
      </div>
    </section>
  );
}

function CtaSection({ t }) {
  return (
    <section className="relative overflow-hidden border-t border-white/10">
      {/* 背景视频 */}
      <HomeVideo
        src="/gate-d/cta-loop.mp4"
        poster="/gate-d/cta-poster.webp"
        autoPlay
        aria-hidden="true"
        className="absolute inset-0 h-full w-full object-cover"
      />
      {/* Mask 遮罩（参考 botgo_ai_old BringIdeasSection） */}
      <div
        aria-hidden="true"
        className="absolute inset-0 bg-cover bg-center"
        style={{ backgroundImage: 'url(/gate-d/cta-mask.png)' }}
      />
      <div
        aria-hidden="true"
        className="absolute inset-0 bg-gradient-to-r from-black/50 via-black/30 to-black/50"
      />
      {/* 内容 */}
      <div className="relative mx-auto flex min-h-screen w-full flex-col justify-center px-5 py-24 sm:px-8 lg:px-12">
        <div className="-mt-48 max-w-4xl pl-[10%]">
          <BrandLockup alt="BotGo" size={40} />
          <h2 className="mt-6 text-4xl font-bold leading-[1.08] sm:text-5xl lg:text-6xl">
            {t('home.ctaTitleLine1')}
            <br />
            {t('home.ctaTitleLine2')}
          </h2>
          <p className="mt-5 max-w-xl text-base text-white/60 sm:text-lg">
            {t('home.ctaBody')}
          </p>
          <div className="mt-10 flex flex-col gap-4 sm:flex-row sm:gap-6">
            <Link
              href="/explore"
              className="w-full rounded-full bg-gradient-to-r from-amber-500 to-orange-600 px-8 py-4 text-center font-semibold text-[#171006] shadow-[0_12px_32px_rgba(245,158,11,.24)] transition-all duration-300 hover:scale-105 hover:from-amber-600 hover:to-orange-700 sm:w-[220px]"
            >
              {t('home.exploreCta')}
            </Link>
            <Link
              href="/create"
              className="w-full rounded-full border-2 border-white px-8 py-4 text-center font-semibold text-white transition-all duration-300 hover:scale-105 hover:border-[#F59E0B] hover:text-[#FBBF24] sm:w-[220px]"
            >
              {t('home.createCta')}
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
}

export default function GateDHome() {
  const t = useTranslations('GateD');
  const tWhatsNew = useTranslations('WhatsNew');
  const initialRails = readHomeRailsCache().data;
  const [generatorExpanded, setGeneratorExpanded] = useState(false);
  const mountedRef = useRef(false);
  const [rails, setRails] = useState(() => initialRails);
  const [railsState, setRailsState] = useState(() => (initialRails ? 'ready' : 'loading'));
  const previewItems = useMemo(() => {
    const uniqueItems = new Map();
    [rails?.featured, rails?.video, rails?.image].forEach((items) => {
      if (!Array.isArray(items)) return;
      items.forEach((item) => {
        if (item?.id && !uniqueItems.has(item.id)) uniqueItems.set(item.id, item);
      });
    });
    return Array.from(uniqueItems.values());
  }, [rails?.featured, rails?.image, rails?.video]);
  const {
    activeIndex: previewIndex,
    openPreview,
    setPreviewIndex,
  } = useInstantPostPreview({ items: previewItems, source: 'home' });

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const loadRails = useCallback(({ force = false } = {}) => {
    const cached = readHomeRailsCache();
    if (cached.data) {
      setRails(cached.data);
      setRailsState('ready');
    } else {
      setRailsState('loading');
    }
    requestHomeRails({ force })
      .then((body) => {
        if (!mountedRef.current) return;
        setRails(body);
        setRailsState('ready');
      })
      .catch(() => {
        if (!mountedRef.current) return;
        const fallback = readHomeRailsCache().data;
        if (fallback) {
          setRails(fallback);
          setRailsState('ready');
        } else {
          setRailsState('error');
        }
      });
  }, []);

  useEffect(loadRails, [loadRails]);

  useEffect(() => {
    const refreshAfterPost = () => loadRails({ force: true });
    window.addEventListener('botgo:post-published', refreshAfterPost);
    return () => window.removeEventListener('botgo:post-published', refreshAfterPost);
  }, [loadRails]);

  const runHomeAction = useCallback((item, action) => {
    openExploreAction(item, action, { targetGeneratorId: 'home-generator' });
  }, []);

  return (
    <HomeMediaProvider suspended={previewIndex != null}>
    <div className="bg-[#070a0f] text-white">
      {/* Hero：标题 + 副标题 + 极简生成器；背景延伸至近全屏并与 What's New 交叉重叠 */}
      <section className="relative min-h-screen overflow-hidden">
        <HomeVideo
          src={SOCIAL_HOME_HERO_MEDIA.videoSrc}
          poster={SOCIAL_HOME_HERO_MEDIA.posterSrc}
          autoPlay
          paused={generatorExpanded}
          aria-hidden="true"
          className="absolute inset-0 h-full w-full object-cover"
        />
        <div className="absolute inset-0 bg-[linear-gradient(to_bottom,rgba(7,10,15,0.55)_0%,rgba(7,10,15,0.28)_40%,rgba(7,10,15,0.5)_72%,#070a0f_100%)]" />
        <div className="relative mx-auto flex min-h-screen max-w-[1500px] flex-col items-center justify-center px-5 pb-56 pt-28 text-center sm:px-8 lg:px-12">
          <p className="text-xs font-semibold tracking-[.24em] text-amber-300">{t('home.eyebrow')}</p>
          <h1 className="mt-5 max-w-none font-playfair text-[clamp(1.875rem,6.5vw,4.5rem)] font-bold leading-[1.1] tracking-tight sm:whitespace-nowrap">
            {t('home.title')}
          </h1>
          <p className="mt-6 max-w-none font-playfair text-xl font-semibold text-white/75 sm:text-2xl lg:whitespace-nowrap lg:text-[1.75rem]">
            {t('home.subtitle')}
          </p>
          <div className="mt-10 w-full max-w-[68rem] [&_[data-adaptive-generator-dock='true']]:mx-auto [&_[data-adaptive-generator-dock='true']]:transition-[max-width] [&_[data-adaptive-generator-dock='true']]:duration-300 [&_[data-landing-generator-expanded='false']]:max-w-[40rem]">
            <HomeClient
              collapseGeneratorOnOutsidePointerDown
              floatingGeneratorBottomClassName="bottom-4 md:bottom-6"
              generatorOnly
              generatorId="home-generator"
              onGeneratorExpandedChange={setGeneratorExpanded}
            />
          </div>
        </div>
      </section>

      {/* What's New：负边距上提，与 Hero 背景交叉重叠，像挡在 Hero 底部前方 */}
      <div className="relative z-10 -mt-44">
        <WhatsNew tWhatsNew={tWhatsNew} />
      </div>

      {railsState === 'loading' ? (
        <p className="mx-auto max-w-[1760px] px-6 py-12 text-white/55">{t('common.loading')}</p>
      ) : null}
      {railsState === 'error' ? (
        <div className="mx-auto max-w-[1760px] px-6 py-12">
          <p className="text-red-300">{t('common.error')}</p>
          <button type="button" onClick={() => loadRails({ force: true })} className="mt-4 rounded-full bg-white px-4 py-2 text-black">
            {t('common.retry')}
          </button>
        </div>
      ) : null}
      {railsState === 'ready' ? (
        <>
          <ModelSpotlight items={rails?.featured} t={t} onOpen={openPreview} onAction={runHomeAction} />
          <MasonryRail
            title={t('home.videoRail')}
            description={t('home.videoRailDescription')}
            items={rails?.video}
            emptyLabel={t('common.empty')}
            onOpen={openPreview}
            onAction={runHomeAction}
            viewAllLabel={t('home.viewAllVideos')}
            viewAllHref="/explore?mediaType=video&sort=featured"
          />
          <MasonryRail
            title={t('home.imageRail')}
            description={t('home.imageRailDescription')}
            items={rails?.image}
            emptyLabel={t('common.empty')}
            onOpen={openPreview}
            onAction={runHomeAction}
            viewAllLabel={t('home.viewAllImages')}
            viewAllHref="/explore?mediaType=image&sort=featured"
          />
        </>
      ) : null}

      <div className="mb-20 mt-24 bg-[#070a0f] md:mt-32">
        <div id="introduction" className="pb-10">
          <LazyMount rootMargin="200px" minHeight="520px">
            <Introduction theme="social" />
          </LazyMount>
        </div>
        <KeyFeatures theme="social" />
      </div>

      <CategoryTiles t={t} />

      <CtaSection t={t} />

      <PublicPostPreview
        items={previewItems}
        activeIndex={previewIndex}
        onIndexChange={setPreviewIndex}
        targetGeneratorId="home-generator"
      />

    </div>
    </HomeMediaProvider>
  );
}
